import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQCardBigText from '../../components/card-big-text';
import UQSpacer from '../../components/spacer';
import UQRow from '../../components/row';
import UQCol from '../../components/col';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const CardBigTextPage = () => {
  const attributes = [
    {
      attribute: 'value',
      type: 'number',
      default: 'undefined',
      description: 'The numeric value to display in the card.',
    },
    {
      attribute: 'header',
      type: 'string',
      default: 'undefined',
      description: 'The header text for the card.',
    },
    {
      attribute: 'icon',
      type: 'string',
      default: 'undefined',
      description: 'The icon to display in the card.',
    },
    {
      attribute: 'iconColor',
      type: 'string',
      default: 'undefined',
      description: 'The color of the icon in the card.',
    },
    {
      attribute: 'fullNumber',
      type: 'boolean',
      default: 'false',
      description: 'Whether to display the full number without formatting.',
    },
    {
      attribute: 'loading',
      type: 'boolean',
      default: 'false',
      description: 'Whether the card is in a loading state.',
    },
    {
      attribute: 'loaderProps',
      type: 'UQLoaderProps',
      default: 'undefined',
      description: 'Props to customize the loader (animation, size, color) when loading is true.',
    },
    {
      attribute: 'className',
      type: 'string',
      default: "''",
      description: 'Additional class names for custom styling.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const loaderAnimations = ['spin', 'clip', 'spin-rainbow', 'clip-rainbow'] as const;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Card Big Text']} />
      <h1>UQCardBigText</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQCardBigText</code> component is a specialized card for displaying numeric values prominently. It supports headers, icons, loading states, and custom styling.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Default Card */}
      <h2>Examples</h2>
      <h3>Default Card</h3>
      <UQSpacer size="s" />
      <UQRow>
        <UQCol span={6}>
          <UQCardBigText
            value={12345}
            header="Default Card"
            icon="info"
            iconColor="support"
          />
        </UQCol>
        <UQCol span={6}>
          <UQCode
            code={`import React from 'react';
import { UQCardBigText } from '@uniqconsulting/ui';

<UQCardBigText
  value={12345}
  header="Default Card"
  icon="info"
  iconColor="support"
/>`}
            enableCopy
          />
        </UQCol>
      </UQRow>
      <UQSpacer size="m" />

      {/* Card with Full Number Formatting */}
      <h3>Card with Full Number Formatting</h3>
      <UQSpacer size="s" />
      <UQRow>
        <UQCol span={6}>
          <UQCardBigText
            value={123456789}
            fullNumber
            header="Full Number Formatting"
            icon="chart"
            iconColor="success"
          />
        </UQCol>
        <UQCol span={6}>
          <UQCode
            code={`import React from 'react';
import { UQCardBigText } from '@uniqconsulting/ui';

<UQCardBigText
  value={123456789}
  fullNumber
  header="Full Number Formatting"
  icon="chart"
  iconColor="success"
/>`}
            enableCopy
          />
        </UQCol>
      </UQRow>
      <UQSpacer size="m" />

      {/* Loading State */}
      <h3>Loading State</h3>
      <UQSpacer size="s" />
      <UQRow>
        <UQCol span={6}>
          <UQCardBigText
            value={0}
            loading
            header="Loading State"
            icon="loader"
            iconColor="warning"
          />
        </UQCol>
        <UQCol span={6}>
          <UQCode
            code={`import React from 'react';
import { UQCardBigText } from '@uniqconsulting/ui';

<UQCardBigText
  value={0}
  loading
  header="Loading State"
  icon="loader"
  iconColor="warning"
/>`}
            enableCopy
          />
        </UQCol>
      </UQRow>
      <UQSpacer size="m" />

      {/* Card with Custom Class */}
      <h3>Card with Custom Class</h3>
      <UQSpacer size="s" />
      <UQRow>
        <UQCol span={6}>
          <UQCardBigText
            value={42}
            header="Custom Class"
            className="custom-card"
            icon="star"
            iconColor="primary"
          />
        </UQCol>
        <UQCol span={6}>
          <UQCode
            code={`import React from 'react';
import { UQCardBigText } from '@uniqconsulting/ui';

<UQCardBigText
  value={42}
  header="Custom Class"
  className="custom-card"
  icon="star"
  iconColor="primary"
/>`}
            enableCopy
          />
        </UQCol>
      </UQRow>
      <UQSpacer size="m" />

      {/* Loader Animation Variations */}
      <h3>Loader Animation Variations</h3>
      <UQSpacer size="s" />
      <UQRow>
        {loaderAnimations.map((animation) => (
          <UQCol span={6} key={animation}>
            <UQCardBigText
              value={999}
              loading
              header={`Loading (${animation})`}
              loaderProps={{ animation, size: 'l' }}
            />
          </UQCol>
        ))}
      </UQRow>
      <UQSpacer size="s" />
      <UQCode
        code={`<UQCardBigText\n  value={999}\n  loading\n  header="Loading (spin-rainbow)"\n  loaderProps={{ animation: 'spin-rainbow', size: 'l' }}\n/>`}
        enableCopy
      />
      <UQSpacer size="m" />
    </>
  );
};

export default CardBigTextPage;